import {
  Button,
  ButtonGroup,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Plus, SquarePen, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import api from "../../axios/api";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomButton from "../../componentsII/Buttons";
import ConfirmationModal from "../../componentsII/ConfirmationModal";
import { PenBoxIcon } from "lucide-react";

const staticBooleansList = [
  { label: "Yes", value: "1" },
  { label: "No", value: "0" },
];

const CompanyEquipmentDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [activeTab, setActiveTab] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [equipmentTypeQueries, setEquipmentTypeQueries] = useState({
    fuel: detailModalData?.consume_fuel,
    gas: detailModalData?.consume_refrigerant,
    refrigrant: detailModalData?.consume_ind_gas,
  });

  const { user } = useSelector((state) => state);
  const year = useSelector((state) => state.user.selectedYear);

  const fetchEquipmentTypes = async ({ fuel, gas, refrigrant }) => {
    const response = await api(
      `/equipment/equipmenttype?fuel=${fuel}&gas=${gas}&refrigrant=${refrigrant}`
    );
    setEquipmentTypes(response.data.data);
  };

  useEffect(() => {
    const fetchLocations = async () => {
      const response = await api(
        `/company/companylocation?company_id=${user.user.company.id}&year_id=${year.id}`
      );
      setLocations(response.data.data);
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchEquipmentTypes({ ...equipmentTypeQueries });
  }, [
    equipmentTypeQueries?.gas,
    equipmentTypeQueries?.fuel,
    equipmentTypeQueries?.refrigrant,
  ]);

  const schema = yup.object().shape({
    equipment_name_code: yup.string().required("Equipment name is required"),
    consume_fuel: yup.number().oneOf([1, 0], "Invalid fuel option").required("Fuel consumption is required"),
    consume_refrigerant: yup.number().oneOf([1, 0], "Invalid refrigerant option").required("Refrigerant consumption is required"),
    consume_ind_gas: yup.number().oneOf([1, 0], "Invalid gas option").required("Industrial gas consumption is required"),
    equipment_type_id: yup.number().required("Equipment type is required"),
    comp_location_id: yup.number().required("Location is required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData?.value,
      equipment_name_code: detailModalData?.label,
      company_id: detailModalData?.company_id,
      comp_data_year_id: detailModalData?.comp_data_year_id,
      consume_fuel: detailModalData?.consume_fuel,
      consume_refrigerant: detailModalData?.consume_refrigerant,
      consume_ind_gas: detailModalData?.consume_ind_gas,
      equipment_type_id:
        detailModalData?.equipment_type_details?.equipment_type_id,
      comp_location_id:
        detailModalData?.comp_location_details?.comp_location_id,
    },
  });

  const handleEdit = () => {
    setIsEditable(!isEditable);
  };
  const handleEditSubmit = async (data) => {
    const body = { ...data };
    try {
      const response = await api.post(
        "/equipment/addUpdateCompanyEquipment",
        body
      );
      if (response.data.status === "200") {
        showNotification(response.data.message, "success");
        setIsEditable(false);

        const equipment_type = equipmentTypes?.find(
          (item) => item.value == data?.equipment_type_id
        );
        const company_location = locations?.find(
          (item) => item.id == data?.comp_location_id
        );
        setDetailModalData((prev) => ({
          ...prev,
          label: data?.equipment_name_code,
          consume_fuel: data?.consume_fuel,
          consume_refrigerant: data?.consume_refrigerant,
          consume_ind_gas: data?.consume_ind_gas,
          equipment_type_details: {
            equipment_type_id: equipment_type?.value,
            equipment_type_name: equipment_type?.label,
          },
          comp_location_details: {
            comp_location_id: company_location?.id,
            comp_location_name: company_location?.loc_name,
          },
        }));

        refetch();
      } else {
        showNotification(response.data.message, "error");
        setIsEditable(false);
      }
    } catch (error) {
      showNotification(error.response.data.message, "error");
      console.error("Error submitting form:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/equipment/deleteCompanyEquipment/${detailModalData?.value}`
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
      }
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="h-full">
      <div>
        <div className="flex border-b border-dashed border-gray-500 ">
          <div className="w-[30%] border-r border-gray-500">
            <img src={detailModalData?.boiler} />
          </div>
          <div className="px-8">
            <h1 className="text-2xl font-bold text-green-600">
              {detailModalData?.label}
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium">
              {detailModalData?.equipment_type_details?.equipment_type_name}
            </p>
            <div className="flex">
              <button></button>
              <button onClick={() => setActiveTab(1)}></button>
              <ButtonGroup
                variant=""
                aria-label="Basic button group"
                style={{ border: "2px solid #388E3C" }}
              >
                <Button
                  style={{
                    backgroundColor: activeTab === 0 ? "#388E3C" : "",
                    color: activeTab === 0 ? "#fff" : "",
                  }}
                  color="green"
                  onClick={() => setActiveTab(0)}
                >
                  Details
                </Button>
                <Button
                  style={{
                    backgroundColor: activeTab === 1 ? "#388E3C" : "",
                    color: activeTab === 1 ? "#fff" : "",
                  }}
                  color="gray"
                  onClick={() => setActiveTab(1)}
                >
                  Usage data
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
        <div>
          {activeTab == 0 ? (
            <div className="px-6">
              <div className="flex justify-between w-full items-center">
                <div className="text-gray-800 my-6 text-xl font-bold">
                  Equipment Details
                </div>
                {!isEditable ? (
                  <div className="cursor-pointer" onClick={handleEdit}>
                    <SquarePen color="#16a34a" />
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <form onSubmit={handleSubmit(handleEditSubmit)}>
                <CompInput
                  title="Equipment Name"
                  value={detailModalData?.label}
                  name="equipment_name_code"
                  isError={!!errors.equipment_name_code}
                  errorText={errors.equipment_name_code?.message}
                  control={control}
                  isEditable={isEditable}
                />
                <CompSelect
                  title="Consume Fuel ?"
                  value={detailModalData?.consume_fuel == 1 ? "Yes" : "No"}
                  name="consume_fuel"
                  isError={!!errors.consume_fuel}
                  errorText={errors.consume_fuel?.message}
                  control={control}
                  isEditable={isEditable}
                  options={staticBooleansList}
                  handleChange={(val) => {
                    setEquipmentTypeQueries((prev) => ({
                      ...prev,
                      fuel: val,
                    }));
                    setValue('equipment_type_id', undefined)
                  }}
                />
                <CompSelect
                  title="Consume Refrigent ?"
                  value={
                    detailModalData?.consume_refrigerant == 1 ? "Yes" : "No"
                  }
                  name="consume_refrigerant"
                  isError={!!errors.consume_refrigerant}
                  errorText={errors.consume_refrigerant?.message}
                  control={control}
                  isEditable={isEditable}
                  options={staticBooleansList}
                  handleChange={(val) => {
                    setEquipmentTypeQueries((prev) => ({
                      ...prev,
                      refrigrant: val,
                    }));
                    setValue('equipment_type_id', undefined)
                  }}
                />
                <CompSelect
                  title="Consume Industrial Gas ?"
                  value={detailModalData?.consume_ind_gas == 1 ? "Yes" : "No"}
                  name="consume_ind_gas"
                  isError={!!errors.consume_ind_gas}
                  errorText={errors.consume_ind_gas?.message}
                  control={control}
                  isEditable={isEditable}
                  options={staticBooleansList}
                  handleChange={(val) => {
                    setEquipmentTypeQueries((prev) => ({
                      ...prev,
                      gas: val,
                    }));
                    setValue('equipment_type_id', undefined)
                  }}
                />
                <CompSelect
                  title="Equipment Type"
                  value={
                    detailModalData?.equipment_type_details?.equipment_type_name
                  }
                  name="equipment_type_id"
                  isError={!!errors.equipment_type_id}
                  errorText={errors.equipment_type_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={equipmentTypes}
                />
                <CompSelect
                  title="Location"
                  value={
                    detailModalData?.comp_location_details?.comp_location_name
                  }
                  name="comp_location_id"
                  isError={!!errors.comp_location_id}
                  errorText={errors.comp_location_id?.message}
                  control={control}
                  isEditable={isEditable}
                  options={locations?.map((item) => {
                    return {
                      ...item,
                      label: item.loc_name,
                      value: item.id,
                    };
                  })}
                />
                <div className="w-full flex justify-end gap-5 items-center py-5">
                  {isEditable ? (
                    <>
                      <CustomButton
                        className="bg-red-500 hover:bg-red-700 text-white "
                        click={() => {
                          setEquipmentTypeQueries({
                            fuel: detailModalData?.consume_fuel,
                            gas: detailModalData?.consume_refrigerant,
                            refrigrant: detailModalData?.consume_ind_gas,
                          });
                          setValue('equipment_type_id', detailModalData?.equipment_type_details?.equipment_type_id)
                          reset();
                          setIsEditable(false);
                        }}
                        text="Cancel"
                      />
                      <CustomButton
                        className="bg-green-600 hover:bg-green-700 text-white "
                        type="submit"
                        text="Submit"
                      />
                    </>
                  ) : (
                    <div className="flex justify-center w-full">
                      <CustomButton
                        className="bg-red-500 hover:bg-red-700 text-white "
                        click={() => setIsDeleteModalOpen(true)}
                        text="Delete"
                      />
                    </div>
                  )}
                </div>
              </form>
            </div>
          ) : (
            // <div>
            //   <div className="grid grid-cols-2 pt-6">
            //     {detailModalData?.fuel_consumption.fuel_quantity ||
            //       detailModalData?.refrigerant_consumption.refrigerant_quantity ||
            //       detailModalData?.industrial_gas_consumption.ind_gas_quantity ? (
            //       <div>
            //         <div className="flex gap-2 items-center">
            //           <p className="font-medium text-[20px]">
            //             {detailModalData?.total_emission ? detailModalData.total_emission : "--"}
            //           </p>
            //           <p>
            //             tCO<sub>2</sub>e
            //           </p>
            //         </div>
            //         <p>Total emission for this equipment</p>
            //       </div>
            //     ) : null}

            //   </div>
            //   <>
            //     {detailModalData?.fuel_consumption.fuel_quantity ||
            //       detailModalData?.refrigerant_consumption.refrigerant_quantity ||
            //       detailModalData?.industrial_gas_consumption.ind_gas_quantity ? (
            //       <>
            //         {detailModalData?.fuel_consumption.fuel_quantity && (<div>
            //           <h3 className="font-medium text-[20px] pt-5">
            //             Fuel Consumption
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.fuel_consumption?.fuel_name}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.fuel_consumption?.fuel_quantity} {detailModalData?.fuel_consumption?.fuel_unit_label}</div>
            //             </div>
            //             <div>{detailModalData?.fuel_consumption?.equipment_fuel_emission}  <span>tCO<sub>2</sub>e</span></div>
            //           </div>
            //         </div>)}

            //         {detailModalData?.refrigerant_consumption.refrigerant_quantity && (<div>
            //           <h3 className="font-medium text-[20px] pt-5">
            //             Refrigerant Consumption
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.refrigerant_consumption?.refrigerant_name}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.refrigerant_consumption?.refrigerant_quantity} {detailModalData?.refrigerant_consumption?.ref_unit_label}</div>
            //             </div>
            //             <div>{detailModalData?.refrigerant_consumption?.refrigerant_emission} <span>tCO<sub>2</sub>e</span></div>
            //           </div>
            //         </div>)}

            //         {detailModalData?.industrial_gas_consumption.ind_gas_quantity && (
            //           <div>
            //             <h3 className="font-medium text-[20px] pt-5">
            //               Industrial Gas Consumption
            //             </h3>
            //             <div
            //               className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //             >
            //               <div className="font-normal text-gray-900">{detailModalData?.industrial_gas_consumption?.ind_gas_name}</div>
            //               <div className="">
            //                 <div className="">{detailModalData?.industrial_gas_consumption?.ind_gas_quantity} {detailModalData?.industrial_gas_consumption?.ind_gas_unit_label}</div>
            //               </div>
            //               <div>{detailModalData?.industrial_gas_consumption?.ind_gas_emission} <span>tCO<sub>2</sub>e</span></div>
            //             </div>
            //           </div>)}
            //       </>) : (
            //       <div className="flex flex-col items-center justify-center h-80">
            //         <img src="/vectors/Equipment_emission_data.jpg" alt="No data" className="w-60 h-60 mb-4" />
            //         <p className="text-gray-500">No usage data available for the equipment</p>
            //       </div>
            //     )}
            //   </>
            // </div>


            //2.
            // <div>
            //   <div className="grid grid-cols-2 pt-6">
            //     {detailModalData?.fuel_consumption.fuel_quantity ||
            //       detailModalData?.refrigerant_consumption.refrigerant_quantity ||
            //       detailModalData?.industrial_gas_consumption.ind_gas_quantity ? (
            //       <div>
            //         <div className="flex gap-2 items-center">
            //           <p className="font-medium text-[20px]">
            //             {detailModalData?.total_emission ? detailModalData.total_emission : "--"}
            //           </p>
            //           <p>
            //             tCO<sub>2</sub>e
            //           </p>
            //         </div>
            //         <p>Total emission for this equipment</p>
            //       </div>
            //     ) : null}

            //   </div>
            //   <>
            //     {detailModalData?.fuel_consumption ||
            //       detailModalData?.refrigerant_consumption ||
            //       detailModalData?.industrial_gas_consumption ? (
            //       <>
            //         {detailModalData?.fuel_consumption && (<div>
            //           <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
            //             Fuel Consumption <PenBoxIcon size={20} />
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.fuel_consumption?.fuel_name ? detailModalData.fuel_consumption?.fuel_name : "--"}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.fuel_consumption?.fuel_quantity ? detailModalData?.fuel_consumption?.fuel_quantity : "--"} {detailModalData?.fuel_consumption?.fuel_unit_label ? detailModalData?.fuel_consumption?.fuel_unit_label : "--"}</div>
            //             </div>
            //             <div>{detailModalData?.fuel_consumption?.equipment_fuel_emission ? detailModalData?.fuel_consumption?.equipment_fuel_emission : "--"}  <span>tCO<sub>2</sub>e</span></div>
            //           </div>
            //         </div>)}

            //         {detailModalData?.refrigerant_consumption && (<div>
            //           <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
            //             Refrigerant Consumption <PenBoxIcon size={20}/>
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.refrigerant_consumption?.refrigerant_name ? detailModalData?.refrigerant_consumption?.refrigerant_name : "--"}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.refrigerant_consumption?.refrigerant_quantity ? detailModalData?.refrigerant_consumption?.refrigerant_quantity : "--"} {detailModalData?.refrigerant_consumption?.ref_unit_label ? detailModalData?.refrigerant_consumption?.ref_unit_label : "--"}</div>
            //             </div>
            //             <div>{detailModalData?.refrigerant_consumption?.refrigerant_emission ? detailModalData?.refrigerant_consumption?.refrigerant_emission : '--'} <span>tCO<sub>2</sub>e</span></div>
            //           </div>
            //         </div>)}

            //         {detailModalData?.industrial_gas_consumption && (
            //           <div>
            //            <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
            //               Industrial Gas Consumption <PenBoxIcon size={20}/>
            //             </h3>
            //             <div
            //               className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //             >
            //               <div className="font-normal text-gray-900">{detailModalData?.industrial_gas_consumption?.ind_gas_name ? detailModalData?.industrial_gas_consumption?.ind_gas_name : "--"}</div>
            //               <div className="">
            //                 <div className="">{detailModalData?.industrial_gas_consumption?.ind_gas_quantity ? detailModalData?.industrial_gas_consumption?.ind_gas_quantity : "--"} {detailModalData?.industrial_gas_consumption?.ind_gas_unit_label ? detailModalData?.industrial_gas_consumption?.ind_gas_unit_label : "--"}</div>
            //               </div>
            //               <div>{detailModalData?.industrial_gas_consumption?.ind_gas_emission ? detailModalData?.industrial_gas_consumption?.ind_gas_emission : "--"} <span>tCO<sub>2</sub>e</span></div>
            //             </div>
            //           </div>)}
            //       </>) : (
            //       <div className="flex flex-col items-center justify-center h-80">
            //         <img src="/vectors/Equipment_emission_data.jpg" alt="No data" className="w-60 h-60 mb-4" />
            //         <p className="text-gray-500">No usage data available for the equipment</p>
            //       </div>
            //     )}
            //   </>
            // </div>


            //3.
            <div>
              <div className="grid grid-cols-2 pt-6">
                {detailModalData?.fuel_consumption.fuel_quantity ||
                  detailModalData?.refrigerant_consumption.refrigerant_quantity ||
                  detailModalData?.industrial_gas_consumption.ind_gas_quantity ? (
                  <div>
                    <div className="flex gap-2 items-center">
                      <p className="font-medium text-[20px]">
                        {detailModalData?.total_emission ? detailModalData.total_emission : "--"}
                      </p>
                      <p>
                        tCO<sub>2</sub>e
                      </p>
                    </div>
                    <p>Total emission for this equipment</p>
                  </div>
                ) : null}

              </div>
              <>
                {detailModalData?.fuel_consumption ||
                  detailModalData?.refrigerant_consumption ||
                  detailModalData?.industrial_gas_consumption ? (
                  <>
                    {detailModalData?.fuel_consumption && (<div>
                      <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
                        Fuel Consumption <PenBoxIcon size={20} />
                      </h3>
                     {detailModalData.fuel_consumption.equipment_fuel_emission ? ( <div className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}>
                        <div className="font-normal text-gray-900">{detailModalData?.fuel_consumption?.fuel_name ? detailModalData.fuel_consumption?.fuel_name : "--"}</div>
                        <div className="">
                          <div className="">{detailModalData?.fuel_consumption?.fuel_quantity ? detailModalData?.fuel_consumption?.fuel_quantity : "--"} {detailModalData?.fuel_consumption?.fuel_unit_label ? detailModalData?.fuel_consumption?.fuel_unit_label : "--"}</div>
                        </div>
                        <div>{detailModalData?.fuel_consumption?.equipment_fuel_emission ? detailModalData?.fuel_consumption?.equipment_fuel_emission : "--"}  <span>tCO<sub>2</sub>e</span></div>
                      </div>): <div className="flex flex-col items-center justify-center h-20">
                        <p className="text-gray-500">No Fuel usage data available for the equipment</p>
                      </div>}
                    </div>)}

                    {detailModalData?.refrigerant_consumption && (<div>
                      <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
                        Refrigerant Consumption <PenBoxIcon size={20} />
                      </h3>
                      {detailModalData.refrigerant_consumption.refrigerant_quantity ? (<div className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}>
                        <div className="font-normal text-gray-900">{detailModalData?.refrigerant_consumption?.refrigerant_name ? detailModalData?.refrigerant_consumption?.refrigerant_name : "--"}</div>
                        <div className="">
                          <div className="">{detailModalData?.refrigerant_consumption?.refrigerant_quantity ? detailModalData?.refrigerant_consumption?.refrigerant_quantity : "--"} {detailModalData?.refrigerant_consumption?.ref_unit_label ? detailModalData?.refrigerant_consumption?.ref_unit_label : "--"}</div>
                        </div>
                        <div>{detailModalData?.refrigerant_consumption?.refrigerant_emission ? detailModalData?.refrigerant_consumption?.refrigerant_emission : '--'} <span>tCO<sub>2</sub>e</span></div>
                      </div>) : <div className="flex flex-col items-center justify-center h-20">
                        <p className="text-gray-500">No Refrigerant usage data available for the equipment</p>
                      </div>}
                    </div>
                    )}

                    {detailModalData?.industrial_gas_consumption && (
                      <div>
                        <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
                          Industrial Gas Consumption <PenBoxIcon size={20} />
                        </h3>
                      {detailModalData?.industrial_gas_consumption.ind_gas_emission ? (  <div
                          className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
                        >
                          <div className="font-normal text-gray-900">{detailModalData?.industrial_gas_consumption?.ind_gas_name ? detailModalData?.industrial_gas_consumption?.ind_gas_name : "--"}</div>
                          <div className="">
                            <div className="">{detailModalData?.industrial_gas_consumption?.ind_gas_quantity ? detailModalData?.industrial_gas_consumption?.ind_gas_quantity : "--"} {detailModalData?.industrial_gas_consumption?.ind_gas_unit_label ? detailModalData?.industrial_gas_consumption?.ind_gas_unit_label : "--"}</div>
                          </div>
                          <div>{detailModalData?.industrial_gas_consumption?.ind_gas_emission ? detailModalData?.industrial_gas_consumption?.ind_gas_emission : "--"} <span>tCO<sub>2</sub>e</span></div>
                        </div>) :  <div className="flex flex-col items-center justify-center h-20">
                        <p className="text-gray-500">No Industrial Gas Consumption data available for the equipment</p>
                      </div>}
                      </div>)}
                  </>) : (
                  <div className="flex flex-col items-center justify-center h-80">
                    <img src="/vectors/Equipment_emission_data.jpg" alt="No data" className="w-60 h-60 mb-4" />
                    <p className="text-gray-500">No usage data available for the equipment</p>
                  </div>
                )}
              </>
            </div>

            //4.            
            // <div>
            //   <div className="grid grid-cols-2 pt-6">
            //     {detailModalData?.fuel_consumption.fuel_quantity ||
            //       detailModalData?.refrigerant_consumption.refrigerant_quantity ||
            //       detailModalData?.industrial_gas_consumption.ind_gas_quantity ? (
            //       <div>
            //         <div className="flex gap-2 items-center">
            //           <p className="font-medium text-[20px]">
            //             {detailModalData?.total_emission ? detailModalData.total_emission : "--"}
            //           </p>
            //           <p>
            //             tCO<sub>2</sub>e
            //           </p>
            //         </div>
            //         <p>Total emission for this equipment</p>
            //       </div>
            //     ) : null}

            //   </div>
            //   <>
            //     {detailModalData?.fuel_consumption ||
            //       detailModalData?.refrigerant_consumption ||
            //       detailModalData?.industrial_gas_consumption ? (
            //       <>
            //         {detailModalData?.fuel_consumption && (<div>
            //           <h3 className="font-medium text-[20px] pt-5">
            //             Fuel Consumption
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.fuel_consumption?.fuel_name}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.fuel_consumption?.fuel_quantity} {detailModalData?.fuel_consumption?.fuel_unit_label}</div>
            //             </div>
            //             <div>
            //               {detailModalData?.fuel_consumption?.equipment_fuel_emission
            //                 ? `${detailModalData.fuel_consumption.equipment_fuel_emission} tCO₂e`
            //                 : null}
            //             </div>

            //           </div>
            //         </div>)}

            //         {detailModalData?.refrigerant_consumption && (<div>
            //           <h3 className="font-medium text-[20px] pt-5">
            //             Refrigerant Consumption
            //           </h3>
            //           <div
            //             className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //           >
            //             <div className="font-normal text-gray-900">{detailModalData?.refrigerant_consumption?.refrigerant_name}</div>
            //             <div className="">
            //               <div className="">{detailModalData?.refrigerant_consumption?.refrigerant_quantity} {detailModalData?.refrigerant_consumption?.ref_unit_label}</div>
            //             </div>
            //             <div>
            //               {detailModalData?.refrigerant_consumption?.refrigerant_emission
            //                 ? `${detailModalData.refrigerant_consumption.refrigerant_emission} tCO₂e`
            //                 : null}
            //             </div>

            //           </div>
            //         </div>)}

            //         {detailModalData?.industrial_gas_consumption && (
            //           <div>
            //             <h3 className="font-medium text-[20px] pt-5">
            //               Industrial Gas Consumption
            //             </h3>
            //             <div
            //               className={`grid grid-cols-3 h-10 w-full items-center border-gray-500`}
            //             >
            //               <div className="font-normal text-gray-900">{detailModalData?.industrial_gas_consumption?.ind_gas_name}</div>
            //               <div className="">
            //                 <div className="">{detailModalData?.industrial_gas_consumption?.ind_gas_quantity} {detailModalData?.industrial_gas_consumption?.ind_gas_unit_label}</div>
            //               </div>
            //               <div>
            //                 {detailModalData?.industrial_gas_consumption?.ind_gas_emission
            //                   ? `${detailModalData.industrial_gas_consumption.ind_gas_emission} tCO₂e`
            //                   : null}
            //               </div>

            //             </div>
            //           </div>)}
            //       </>) : (
            //       <div className="flex flex-col items-center justify-center h-80">
            //         <img src="/vectors/Equipment_emission_data.jpg" alt="No data" className="w-60 h-60 mb-4" />
            //         <p className="text-gray-500">No usage data available for the equipment</p>
            //       </div>
            //     )}
            //   </>
            // </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Equipment</div>}
      />
    </div>
  );
};

export default CompanyEquipmentDetailModal;

const CompInput = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  required = true,
}) => {
  return (
    <div
      className={`grid grid-cols-2 h-16 w-full items-center ${isBorderBottom ? "border-b border-gray-500" : ""
        }`}
    >
      <div className="font-medium text-gray-900 flex gap-1 ">{title}<span className={required ? "text-red-500" : "invisible"}>*</span></div>
      <div className="">
        {isEditable ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={isError}
                helperText={errorText}
                margin="none"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    padding: 0,
                    "& .MuiInputBase-input": {
                      padding: "10px",
                    },
                  },
                }}
                sx={{
                  padding: 0,
                  margin: 0,
                }}
              />
            )}
          />
        ) : (
          <div className="">{value}</div>
        )}
      </div>
    </div>
  );
};

const CompSelect = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  options,
  required = true,
  handleChange = () => { },
}) => {
  return (
    <div
      className={`grid grid-cols-2 h-16 w-full items-center ${isBorderBottom ? "border-b border-gray-500" : ""
        }`}
    >
      <div className="font-medium text-gray-900 flex gap-1">{title}<span className={required ? "text-red-500" : "invisible"}>*</span></div>
      <div className="">
        {isEditable ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={isError}>
                <Select
                  {...field}
                  sx={{
                    padding: 0, // Removes padding in the Select component itself
                    "& .MuiInputBase-input": {
                      padding: "10px !important", // Removes padding from the input area
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value, field);
                  }}
                  renderValue={(selected) => {
                    const selectedVal = options.find((item) => item?.value == selected)
                    if (selectedVal == undefined) {
                      return <em>Select option</em>;
                    }
                    return selectedVal?.label;
                  }}
                >
                  {options?.length == 0 ?
                    <>
                      <MenuItem value={undefined} disabled>
                        Select option
                      </MenuItem>
                    </>
                    : <></>}
                  {options?.map((make) => (
                    <MenuItem key={make.value} value={make.value}>
                      {make.label}
                    </MenuItem>
                  ))}

                </Select>
                {errorText && <p className="text-xs text-red-500">{errorText}</p>}
              </FormControl>
            )}
          />
        ) : (
          <div className="">{value}</div>
        )}
      </div>
    </div>
  );
};
